// Adapted from from '@atlassian/jira-issue-table/src/common/fields/date';
import React, { forwardRef, memo } from 'react';
import { parseISO } from 'date-fns';
import { graphql, useFragment } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import { FormattedDate } from '@atlassian/jira-intl';
import { DateInlineEditViewWithIsEditable } from '@atlassian/jira-issue-field-date-inline-edit-full/src/ui/date/index.tsx';
import type { DateInlineEditCell$key } from '@atlassian/jira-relay/src/__generated__/DateInlineEditCell.graphql';
import type { DateInlineEditCell_DateInlineEditCell$key } from '@atlassian/jira-relay/src/__generated__/DateInlineEditCell_DateInlineEditCell.graphql';
import { KNOWN_COLUMN_TYPES } from '@atlassian/jira-issue-table-common/src/constants.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useLazyLoadEditability } from '../../../../services/lazy-editability-loader/index.tsx';
import ErrorCell from '../../error-cell/index.tsx';
import { INLINE_EDIT_ANALYTICS_ATTRIBUTES } from '../common/inline-edit-analytics-attributes.tsx';
import { useIsSingleLineRowHeightEnabled } from '../../../../controllers/features/selectors.tsx';
import { DUPLICATE_FIELD_EDIT_WIDTH } from '../../../constants.tsx';

const parseDate = (dateString: string | null): Date | null => {
	if (dateString !== null && dateString !== '') {
		const date = parseISO(dateString);
		if (date.toString() !== 'Invalid Date') {
			return date;
		}
	}
	return null;
};

const formattedDate = (dateString: string | null) => {
	const date = parseDate(dateString);
	if (!date) {
		return null;
	}
	return <FormattedDate value={date} year="numeric" month="short" day="numeric" />;
};

export type Props = {
	fieldRef: DateInlineEditCell$key;
	isRequired?: boolean;
};

export const DateCell = memo<Props>(({ fieldRef, isRequired }: Props) => {
	const data = useFragment<DateInlineEditCell$key>(
		graphql`
			fragment DateInlineEditCell on JiraIssueField
			@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
				__typename
				fieldId
				... on JiraDatePickerField {
					date
					...DateInlineEditCell_DateInlineEditCell
						@arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
				}
				... on JiraDateTimePickerField {
					dateTime
				}
			}
		`,
		fieldRef,
	);

	if (data.__typename === 'JiraDatePickerField' && data.fieldId === KNOWN_COLUMN_TYPES.DUE_DATE) {
		return <DateInlineEditCell fieldRef={data} />;
	}

	let date: string | null = null;
	if (data.__typename === 'JiraDatePickerField') {
		date = data.date ?? null;
	} else if (data.__typename === 'JiraDateTimePickerField') {
		date = data.dateTime ?? null;
	}

	if (isRequired && date == null) {
		return <ErrorCell cellType="date" reason="Missing required fields" />;
	}

	return <Box xcss={wrapperStyles}>{formattedDate(date)}</Box>;
});

type DateInlineEditCellProps = {
	fieldRef: DateInlineEditCell_DateInlineEditCell$key;
	isFirstField?: boolean;
	hasDuplicates?: boolean;
};

export const DateInlineEditCell = ({
	fieldRef,
	isFirstField,
	hasDuplicates,
}: DateInlineEditCellProps) => {
	const data = useFragment<DateInlineEditCell_DateInlineEditCell$key>(
		graphql`
			fragment DateInlineEditCell_DateInlineEditCell on JiraDatePickerField
			@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
				id
				lazyIsEditableInIssueView @include(if: $isInlineEditingEnabled)
				...date_issueFieldDateInlineEditFull_DateInlineEditViewWithIsEditable_fragmentRef
			}
		`,
		fieldRef,
	);

	const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();

	// If the property does not exist in fragment data then we know it has been conditionally excluded because inline
	// editing is disabled. In this case we explicitly set isEditable to false to bypass the lazy editability query.
	const isEditable = 'lazyIsEditableInIssueView' in data ? data.lazyIsEditableInIssueView : false;
	useLazyLoadEditability(data.id, isEditable);

	if (hasDuplicates && isSingleLineRowHeightEnabled) {
		return (
			<DateInlineEditViewWithIsEditable
				attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
				spacing="default"
				editViewPopup
				editViewPopupAlignBlock="start"
				// @ts-expect-error: to unblock React 18.2.0 -> 18.3.1 version bump
				editViewPopupTarget={isFirstField ? CustomTarget : undefined}
				editViewPopupWidth={DUPLICATE_FIELD_EDIT_WIDTH}
				fragmentRef={data}
				isEditable={isEditable ?? false}
				readViewFitContainerHeight
				shouldShowHoverPopover={isFirstField && isSingleLineRowHeightEnabled}
			/>
		);
	}

	return (
		<DateInlineEditViewWithIsEditable
			attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
			spacing="default"
			editViewPopup
			editViewPopupAlignBlock="center"
			fragmentRef={data}
			isEditable={isEditable ?? false}
			readViewFitContainerHeight
			shouldShowHoverPopover={isSingleLineRowHeightEnabled}
		/>
	);
};

const wrapperStyles = xcss({
	display: '-webkit-box',
	WebkitBoxOrient: 'vertical',
	WebkitLineClamp: 2,
	maxWidth: '100%',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

const CustomTarget = forwardRef<HTMLDivElement, {}>((_, ref) => {
	return (
		<Box
			xcss={fg('nin_issue_table_rtl_fix') ? customTargetStyles : customTargetStylesOld}
			ref={ref}
		/>
	);
});

const customTargetStylesOld = xcss({
	width: 'space.0',
	position: 'absolute',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	top: 'space.0',
});

const customTargetStyles = xcss({
	width: 'space.0',
	position: 'absolute',
	insetBlockStart: 'space.0',
});
